import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { ReaImportCheckPresenter } from './presenters/rea-import-check.presenter';
import { ReaImportDataPresenter } from './presenters/rea-import-data.presenter';
import { ReaImportErrorPresenter } from './presenters/rea-import-error.presenter';
import { ReaImportSummaryPresenter } from './presenters/rea-import-summary.presenter';
import { ReaImportViewMapper } from './presenters/rea-import-view.mapper';
import { ReaSupportEmailPresenter } from './presenters/rea-support-email.presenter';
import { ReaImportPayloadBuilder } from './services/rea-import-payload.builder';
import { ReaImportAuditFacade } from './services/rea-import-audit.facade';
import { ReaImportNavigationService } from './services/rea-import-navigation.service';
import { ReaImportFlowFacade } from './services/rea-import-flow.facade';

import { BaseResponseModel } from 'src/app/models/base-response.model';
import {
  HistoricoREAModel,
  ReaCheckResponse,
  ReaCheckViewState,
  ReaErrorData,
  ReaExplotacion,
  ReaImportAuditDetailResponse,
  ReaImportAuditRecord,
  ReaImportChange,
  ReaMapInsights,
  ReaSuggestedMapLayer,
  ReaImportData,
  ReaAuditSummaryItem,
  ReaChangeDigestItem,
  ReaImportResult,
  ReaImportRichSummary,
  ReaMetricItem,
  ReaImportViewState,
  ReaMessageState,
  ReaModalModel,
  ReaRecoveryAction,
  ReaTechnicalDetailItem,
  ReaUserMessage,
  ReaImportAuditDetail,
  ReaReviewReasonItem,
} from 'src/app/models/fincas/rea.model';

@Component({
  selector: 'app-rea-import-modal',
  templateUrl: './rea-import-modal.component.html',
  styleUrls: ['./rea-import-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  standalone: false,
  providers: [ReaImportAuditFacade, ReaImportNavigationService, ReaImportFlowFacade],
})
export class ReaImportModalComponent {
  @Input({ required: true })
  public visible = false;

  @Output()
  public readonly visibleChange = new EventEmitter<boolean>();

  @Input({ required: true })
  public model!: ReaModalModel;

  /** If parent provides it, we show it; otherwise we fetch on dialog open. */
  @Input()
  public historicoREA: BaseResponseModel<HistoricoREAModel> | null = null;

  @Output()
  public readonly imported = new EventEmitter<BaseResponseModel<ReaImportData>>();

  @Output()
  public readonly reviewRequested = new EventEmitter<ReaReviewReasonItem[]>();

  @Output()
  public readonly fincaOpenRequested = new EventEmitter<number>();

  @Input()
  public fincaDetailUrlPrefix = '/dashboard/editar-finca';

  @Input()
  public parcelaDetailUrlPrefix = '/dashboard/editar-parcela';

  /** Dialog breakpoints. */
  public readonly REA_DIALOG_BREAKPOINTS: Readonly<Record<string, string>> = {
    '1600px': '50vw',
    '1199px': '68vw',
    '768px': '88vw',
    '480px': '96vw',
  };

  private readonly RECENT_CHANGES_LIMIT = 6;

  private readonly markForCheck = (): void => {
    this.cdr.markForCheck();
  };

  constructor(
    private readonly flowFacade: ReaImportFlowFacade,
    private readonly auditFacade: ReaImportAuditFacade,
    private readonly navigationService: ReaImportNavigationService,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  /** Hide "Importar" after receiving a final import response. */
  public get canSend(): boolean {
    return this.flowFacade.canSend();
  }

  /** Returns true when the current FEGA check requires the user to choose a REA. */
  public get requiresReaSelection(): boolean {
    return this.richCheckResult?.requiresSelection === true;
  }

  /** Returns true when the import action can be executed safely. */
  public get canImport(): boolean {
    return this.flowFacade.canImport({
      requiresReaSelection: this.requiresReaSelection,
      hasSelectedAvailableRea: this.hasSelectedAvailableRea,
      reaCount: this.richCheckResult?.reaCount ?? null,
      hasReaCodeForImport: this.hasReaCodeForImport,
      allowsTestFallbackImport: this.allowsTestFallbackImport,
    });
  }

  public get showSpinner(): boolean {
    return this.flowFacade.showSpinner;
  }

  public get messageState(): ReaMessageState {
    return this.flowFacade.messageState;
  }

  public get importResult(): BaseResponseModel<ReaImportData> | null {
    return this.flowFacade.importResult;
  }

  public get checkResult(): BaseResponseModel<ReaImportData> | null {
    return this.flowFacade.checkResult;
  }

  public get selectedRea(): string {
    return this.flowFacade.selectedRea;
  }

  public get recoveryAction(): ReaRecoveryAction | null {
    return this.flowFacade.recoveryAction;
  }

  /** Header helper for templates. */
  public get lastReaFecha(): string {
    return this.historicoREA?.firstOrNull()?.fecha ?? '';
  }

  /** Current rich import result returned by the backend, if available. */
  public get richImportResult(): ReaImportResult | null {
    return ReaImportDataPresenter.richImportResult(this.importResult);
  }

  /** Current FEGA/NIF check result returned by the backend, if available. */
  public get richCheckResult(): ReaCheckResponse | null {
    return ReaImportDataPresenter.richCheckResponse(this.checkResult);
  }

  /** Summary returned by the new REA backend contract. */
  public get richSummary(): ReaImportRichSummary | null {
    return this.richImportResult?.summary ?? null;
  }

  /** Import identifier useful for support. */
  public get importId(): string {
    return this.richImportResult?.importId ?? '';
  }

  /** REA code confirmed/imported by the backend. */
  public get importedRea(): string {
    return this.richImportResult?.codigoRea ?? (this.selectedRea || this.model.codigo_REA || '');
  }

  /** Identifier of the finca created or updated by the REA import. */
  public get importedFincaId(): number | null {
    return ReaImportDataPresenter.importedFincaId(this.richImportResult);
  }

  /** True when the footer can offer direct access to the imported finca. */
  public get canOpenImportedFinca(): boolean {
    return this.importResult?.isOk === true && this.importedFincaId !== null;
  }

  /** Label used by the footer close button. It only closes the modal. */
  public get closeButtonLabel(): string {
    return this.importResult?.statusCode ? 'Cerrar ventana' : 'Cancelar sin importar';
  }

  /** User-facing value messages generated by the backend. */
  public get valueMessages(): string[] {
    if (this.richImportResult == null && this.richCheckResult != null) {
      const notice = this.checkViewState.notice;

      return notice !== '' ? [notice] : [];
    }

    const messages = this.richImportResult?.valueMessages ?? [];

    return messages
      .map((message) => ReaImportDataPresenter.customerText(message))
      .filter((message) => message.length > 0);
  }

  /** User-facing warnings after the import. Initial check warnings are merged into the main message to avoid repetition. */
  public get userWarnings(): string[] {
    const warnings = this.richImportResult?.warnings ?? [];

    return warnings
      .map((warning) => ReaImportDataPresenter.warningToText(warning))
      .filter((warning) => warning.length > 0);
  }

  /** True when the response is successful but includes relevant user warnings. */
  public get hasUserWarnings(): boolean {
    return this.userWarnings.length > 0;
  }

  /** Top result message severity for the user. */
  public get resultSeverity(): 'success' | 'warn' {
    return this.hasUserWarnings ? 'warn' : 'success';
  }

  /** True when the current attempt has failed and the UI must focus on the solution. */
  public get hasCurrentError(): boolean {
    return this.messageState === 'error' && this.importResult != null;
  }

  /** Main title shown before secondary sections. */
  public get mainStatusTitle(): string {
    return this.mainViewState.title;
  }

  /** Main user-facing explanation for the current state. */
  public get mainStatusDescription(): string {
    return this.mainViewState.description;
  }

  /** Presentation state consumed by the template. */
  public get mainViewState(): ReaImportViewState {
    return ReaImportViewMapper.map({
      status: this.messageState,
      hasCurrentError: this.hasCurrentError,
      userMessage: this.currentUserMessage,
      statusMessage: this.importResult?.statusMessage ?? '',
      hasUserWarnings: this.hasUserWarnings,
      customerMetrics: this.customerSummaryMetrics,
      customerWarnings: this.userWarnings,
      technicalDetails: this.currentTechnicalDetails,
    });
  }

  /** Suggested next step for failed imports. */
  public get currentErrorNextStep(): string {
    return this.mainViewState.nextStep ?? 'Revisa los datos introducidos y vuelve a intentarlo.';
  }

  /** First user-facing value sentence, merged into the main result. */
  public get primaryValueMessage(): string {
    return this.valueMessages[0] ?? '';
  }

  /** User-focused metrics only. Technical counters are moved to the support details block. */
  public get customerSummaryMetrics(): ReaMetricItem[] {
    return ReaImportSummaryPresenter.customerMetrics(this.richSummary);
  }

  /** Support/development metrics kept out of the customer view. */
  public get technicalSummaryMetrics(): ReaMetricItem[] {
    return this.hasCurrentError ? ReaImportSummaryPresenter.technicalMetrics(this.richSummary) : [];
  }

  /** True when the initial REA check needs an explicit customer action. */
  public get showCheckSelection(): boolean {
    const check = this.richCheckResult;

    if (check == null || this.richImportResult != null) {
      return false;
    }

    return check.requiresSelection || (!this.hasReaCodeForImport && check.reaCount === 0 && !this.allowsTestFallbackImport);
  }

  /** True when previous imports should be offered as a secondary review after an import attempt. */
  public get showAuditHistorySection(): boolean {
    return this.richImportResult != null && this.hasAuditHistory;
  }

  /** Error/support details prepared for the technical details block. */
  public get currentTechnicalDetails(): ReaTechnicalDetailItem[] {
    return ReaImportErrorPresenter.technicalDetails(this.importResult, this.importResult?.statusCode ?? null);
  }

  private get currentErrorData(): ReaErrorData | null {
    return ReaImportErrorPresenter.errorData(this.importResult);
  }

  private get currentUserMessage(): ReaUserMessage | null {
    return this.currentErrorData?.userMessage ?? null;
  }

  /** REA code that will be sent to the backend when importing. */
  public get reaCodeForImport(): string {
    return String(this.selectedRea || this.model.codigo_REA || '').trim();
  }

  /** Returns true when the import request has a syntactically valid REA code. */
  public get hasReaCodeForImport(): boolean {
    return ReaImportPayloadBuilder.isValidReaCode(this.reaCodeForImport);
  }

  /** Allows import without confirmed REA only when the backend explicitly enables test fallback mode. */
  public get allowsTestFallbackImport(): boolean {
    const check = this.richCheckResult;
    const rawData = this.asRecord(this.checkResult?.firstOrNull() ?? null);
    const rawSummary = this.asRecord(rawData['summary']);
    const checkSummary = this.asRecord(check?.summary ?? null);

    if (check?.requiresSelection === true) {
      return false;
    }

    const hasNoFegaRea = check?.reaCount === 0
      || this.asNumber(rawData['reaCount']) === 0
      || this.asNumber(rawSummary['explotacionesEncontradas']) === 0;

    const fallbackEnabled = this.asBoolean(rawData['testFallbackEnabled'])
      || this.asBoolean(rawSummary['testFallbackEnabled'])
      || this.asBoolean(rawSummary['fegaValidationSkipped'])
      || this.asBoolean(checkSummary['testFallbackEnabled'])
      || this.asBoolean(checkSummary['fegaValidationSkipped']);

    return hasNoFegaRea && fallbackEnabled;
  }

  /** Helps users understand why the import button stays disabled after a stale or invalid REA selection. */
  public get invalidSelectedReaMessage(): string {
    if (!this.requiresReaSelection) {
      return '';
    }

    if (this.selectedRea.trim() === '' || this.hasSelectedAvailableRea) {
      return '';
    }

    return 'Selecciona una explotación de la lista para continuar.';
  }

  /** Customer-facing state for the initial availability check. */
  public get checkViewState(): ReaCheckViewState {
    return ReaImportCheckPresenter.map(this.richCheckResult, this.hasReaCodeForImport);
  }

  /** Recent parcel changes shown as a compact before/after digest. */
  public get recentChanges(): ReaImportChange[] {
    return (this.richImportResult?.changes ?? []).slice(0, this.RECENT_CHANGES_LIMIT);
  }

  /** Aggregated map and geometry information returned by the backend. */
  public get mapInsights(): ReaMapInsights | null {
    return this.richImportResult?.mapInsights ?? null;
  }

  /** Suggested map layers that can later be wired to the map screen. */
  public get suggestedMapLayers(): ReaSuggestedMapLayer[] {
    return (this.mapInsights?.suggestedLayers ?? []).filter((layer) => layer.count > 0);
  }

  public get hasMapInsights(): boolean {
    return this.suggestedMapLayers.length > 0;
  }

  /** Human explanation of why some parcels should be reviewed after import. */
  public get reviewReasons(): ReaReviewReasonItem[] {
    const backendReasons = this.richImportResult?.reviewReasons;

    return Array.isArray(backendReasons) && backendReasons.length > 0
      ? backendReasons
      : ReaImportSummaryPresenter.reviewReasons(this.richSummary);
  }

  /** Number used in the customer summary and review explanation so both views stay aligned. */
  public get reviewRequiredCount(): number {
    const backendCount = this.richImportResult?.reviewRequiredCount;

    return typeof backendCount === 'number'
      ? backendCount
      : ReaImportSummaryPresenter.reviewRequiredCount(this.richSummary);
  }

  /** Aggregate digest for all detected changes in the current import. */
  public get changeDigestMetrics(): ReaChangeDigestItem[] {
    return ReaImportSummaryPresenter.changeDigest(this.richImportResult?.changes ?? []);
  }

  /** Explotations available after the NIF check. */
  public get availableExplotaciones(): ReaExplotacion[] {
    return this.richCheckResult?.explotaciones ?? [];
  }

  /** True when previous imports are currently visible. */
  public get showAuditHistoryPanel(): boolean {
    return this.auditFacade.showPanel;
  }

  /** Latest persisted audit records for the imported or selected REA. */
  public get auditRecords(): ReaImportAuditRecord[] {
    return this.auditFacade.records;
  }

  /** True when the optional audit tables are available and there are persisted records. */
  public get hasAuditHistory(): boolean {
    return this.auditFacade.hasHistory;
  }

  /** Aggregated support stats for previous imports. */
  public get auditStatsMetrics(): ReaAuditSummaryItem[] {
    return this.auditFacade.statsMetrics;
  }

  /** Currently selected audit import identifier. */
  public get selectedAuditImportId(): string {
    return this.auditFacade.selectedImportId;
  }

  /** True while a historical import detail is loading. */
  public get auditDetailLoading(): boolean {
    return this.auditFacade.detailLoading;
  }

  /** Detailed persisted before/after changes for the selected audit record. */
  public get selectedAuditDetail(): ReaImportAuditDetailResponse | null {
    return this.auditFacade.selectedDetail;
  }

  /** Detailed changes shown in the audit detail section. */
  public get selectedAuditChanges(): ReaImportAuditDetail[] {
    return this.auditFacade.selectedChanges;
  }

  public onDialogShow(): void {
    this.getLastRegisterREA();
    this.loadAuditHistory(this.model.codigo_REA ?? '');

    this.checkReaAvailability();
  }

  public hide(): void {
    this.visible = false;
    this.visibleChange.emit(false);
    this.resetState();
    this.cdr.markForCheck();
  }

  public requestParcelReview(reasons: ReaReviewReasonItem[]): void {
    this.reviewRequested.emit(reasons);
    this.navigationService.openReviewParcelas({
      reasons,
      fincaId: this.importedFincaId,
      importId: this.importId,
      changes: this.richImportResult?.changes ?? [],
      onBeforeNavigate: (): void => this.hide(),
    });
  }

  public openImportedFinca(): void {
    const fincaId = this.importedFincaId;

    if (fincaId === null) {
      return;
    }

    this.fincaOpenRequested.emit(fincaId);
    this.navigationService.openImportedFinca(fincaId, (): void => this.hide());
  }

  /** Triggered by (onShow) in the dialog. */
  public getLastRegisterREA(): void {
    this.auditFacade.loadLastRegisterRea(
      this.model.codigo_REA ?? '',
      this.historicoREA,
      (response: BaseResponseModel<HistoricoREAModel>): void => {
        this.historicoREA = response;
      },
      this.markForCheck,
    );
  }

  public checkReaAvailability(): void {
    const request = this.flowFacade.checkAvailability(this.model);

    if (request == null) {
      this.cdr.markForCheck();
      return;
    }

    request.subscribe((result) => {
      if (result.status === 'error') {
        this.auditFacade.closePanel();
        this.cdr.markForCheck();
        return;
      }

      if (
        result.status === 'success'
        && !result.check?.requiresSelection
        && ReaImportPayloadBuilder.isValidReaCode(result.selectedRea)
      ) {
        this.loadAuditHistory(result.selectedRea);
      }

      this.cdr.markForCheck();
    });
  }

  public exportREA(): void {
    if (!this.canImport) {
      return;
    }

    const request = this.flowFacade.importRea(this.model);

    if (request == null) {
      this.cdr.markForCheck();
      return;
    }

    request.subscribe((result) => {
      if (result.response == null) {
        this.cdr.markForCheck();
        return;
      }

      this.auditFacade.closePanel();
      this.imported.emit(result.response);

      if (result.response.isOk) {
        this.loadAuditHistory(this.importedRea);
      }

      this.cdr.markForCheck();
    });
  }

  public setSelectedRea(selectedRea: string): void {
    const normalizedSelectedRea = selectedRea.trim();

    this.flowFacade.setSelectedRea(normalizedSelectedRea);
    this.auditFacade.resetHistory();

    if (ReaImportPayloadBuilder.isValidReaCode(normalizedSelectedRea)) {
      this.loadAuditHistory(normalizedSelectedRea);
    }

    this.cdr.markForCheck();
  }

  public loadAuditHistory(codigoRea: string): void {
    this.auditFacade.loadHistory(codigoRea, this.markForCheck);
  }

  public toggleAuditHistory(): void {
    this.auditFacade.togglePanel();
    this.cdr.markForCheck();
  }

  public get currentErrorSupportEmailHref(): string {
    return ReaSupportEmailPresenter.currentErrorHref({
      fincaName: this.model.nombre_finca,
      nif: this.model.titular_nif,
      codigoRea: this.importedRea || this.reaCodeForImport,
      title: this.mainStatusTitle,
      description: this.mainStatusDescription,
      nextStep: this.currentErrorNextStep,
      technicalDetails: this.currentTechnicalDetails,
    });
  }

  public showAuditDetail(record: ReaImportAuditRecord): void {
    this.auditFacade.showDetail(record, this.markForCheck);
  }

  public closeAuditDetail(): void {
    this.auditFacade.closeDetail();
    this.cdr.markForCheck();
  }

  private resetState(): void {
    this.flowFacade.reset();
    this.auditFacade.reset();
  }

  private get hasSelectedAvailableRea(): boolean {
    const selectedRea = this.selectedRea.trim();

    if (selectedRea === '') {
      return false;
    }

    return this.availableExplotaciones.some((explotacion) => explotacion.codigoRea.trim() === selectedRea);
  }

  private asRecord(value: unknown): Record<string, unknown> {
    return value !== null && typeof value === 'object' ? value as Record<string, unknown> : {};
  }

  private asBoolean(value: unknown): boolean {
    return value === true;
  }

  private asNumber(value: unknown): number | null {
    return typeof value === 'number' ? value : null;
  }
}
